import React, { useState } from "react";
import {
  Button,
  Content,
  Dialog,
  DialogContainer,
  Divider,
  Flex,
  Heading,
  Text,
  TextArea,
  View,
} from "@adobe/react-spectrum";
import Close from "@spectrum-icons/workflow/Close";
import CallbackSuccessCard from "./CallbackSuccessCard";
import { cancelCallbackApi, cancelWebMeetingApi } from "../../api/session-api";
import { toast } from "react-toastify";
import Emitter from "../../emitter";
import { API_CALLBACK_DASHBOARD, TELEMETRY_ERROR } from "../../constants/events";

interface CancelConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  callbackData?: any;
  onCancelSuccess?: () => void;
  userEmail?: string;
  userName?: string;
}

const CancelConfirmationDialog: React.FC<CancelConfirmationDialogProps> = ({
  isOpen,
  onClose,
  callbackData,
  onCancelSuccess,
  userEmail,
  userName,
}) => {
  const isScheduledWeb = `${callbackData?.callbackType || ""}` === "SCHEDULED_WEB";
  const [cancelReason, setCancelReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [successData, setSuccessData] = useState<{ reason?: string } | null>(null);

  const getWebMeetingId = () => callbackData?.requestId || "";

  const getWebMeetingErrorMessage = (error: any, fallback: string) => {
    const body = error?.body;
    if (body?.fieldErrors) {
      return Object.values(body.fieldErrors).join(", ");
    }
    if (body?.error === "Invalid session") {
      return "Session expired. Please sign in again.";
    }
    if (body?.error === "Unable to reach web meeting service") {
      return "Unable to reach web meeting service. Please try again.";
    }
    return body?.message || error?.message || fallback;
  };

  const resetForm = () => {
    setCancelReason("");
    setIsSubmitting(false);
    setShowSuccessCard(false);
    setSuccessData(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSuccessCardClose = () => {
    setShowSuccessCard(false);
    setSuccessData(null);
    handleClose();
  };

  const handleConfirm = async () => {
    if (!cancelReason.trim() || isSubmitting || !callbackData) {
      return;
    }

    setIsSubmitting(true);
    const trimmedReason = cancelReason.trim();

    try {
      if (isScheduledWeb) {
        const payload = {
          webMeetingId: getWebMeetingId(),
          caseId: callbackData?.callbackAttributes?.CASEID,
          cancelReason: trimmedReason,
          canceledByName: userName || undefined,
          canceledByEmail: userEmail || undefined,
          surface: "iHub",
        };

        const res = await cancelWebMeetingApi(payload);

        if (res?.success) {
          Emitter.emit(TELEMETRY_ERROR, {
            type: "notif",
            chan: TELEMETRY_ERROR,
            body: {
              type: API_CALLBACK_DASHBOARD,
              info: "Web meeting cancelled successfully by " + userEmail,
              data: payload,
            },
          });

          setSuccessData({ reason: trimmedReason });
          setShowSuccessCard(true);

          if (onCancelSuccess) {
            onCancelSuccess();
          }
        } else {
          toast.error(res?.message || "Failed to cancel web meeting");
          handleClose();
        }
        return;
      }

      const payload = {
        contactId: callbackData?.contactId,
        caseId: callbackData?.callbackAttributes?.CASEID,
        phoneNumber: callbackData?.phoneNumber,
        cancelReason: trimmedReason,
      };

      const res = await cancelCallbackApi(payload);

      if (res?.status === "SUCCESS") {
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_ERROR,
          body: {
            type: API_CALLBACK_DASHBOARD,
            info: "Callback Cancelled successfully by " + userEmail,
            data: payload,
          },
        });

        setSuccessData({ reason: trimmedReason });
        setShowSuccessCard(true);

        if (onCancelSuccess) {
          onCancelSuccess();
        }
      } else {
        toast.error("Failed to cancel callback");
        handleClose();
      }
    } catch (error: any) {
      console.error("Cancel error:", error);
      toast.error(
        isScheduledWeb
          ? getWebMeetingErrorMessage(error, "Failed to cancel web meeting")
          : "Failed to cancel callback"
      );
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <CallbackSuccessCard
        isOpen={showSuccessCard}
        onClose={handleSuccessCardClose}
        title={isScheduledWeb ? "Web meeting is cancelled" : "Callback is cancelled"}
        subtitle={
          isScheduledWeb
            ? "Your web meeting has been cancelled with the below details"
            : "Your callback has been cancelled with the below details"
        }
        details={successData || undefined}
        type="cancel"
      />

      <DialogContainer onDismiss={handleClose}>
        {!showSuccessCard && (
          <Dialog size="M">
            <Heading>
              <Flex alignItems="center" justifyContent="space-between">
                <Text>{isScheduledWeb ? "Cancel Web Meeting" : "Cancel Callback"}</Text>
                <span
                  style={{
                    cursor: "pointer",
                    fontSize: "24px",
                    color: "#999",
                    padding: "8px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "32px",
                    height: "32px",
                  }}
                  onClick={handleClose}
                >
                  <Close />
                </span>
              </Flex>
            </Heading>
            <Divider />
            <Content>
              <View padding="size-200">
                <Flex direction="column" gap="size-200">
                  <Text UNSAFE_style={{ fontSize: "16px", fontWeight: "600", color: "#323232" }}>
                    {isScheduledWeb
                      ? "Are you sure you want to cancel this web meeting?"
                      : "Are you sure you want to cancel this callback?"}
                  </Text>

                  <View>
                     <View UNSAFE_style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                     <Text UNSAFE_style={{ fontSize: "12px", marginBottom: "8px", display: "block" }}>
                        Reason for cancellation *
                      </Text>
                      <Text
                        UNSAFE_style={{
                          fontSize: "12px",
                          color: "#6b7280",
                        }}
                      >
                        {cancelReason.length}/500
                      </Text>
                     </View>
                    <TextArea
                      placeholder={
                        isScheduledWeb
                          ? "Please provide a reason for cancelling this web meeting..."
                          : "Please provide a reason for cancelling this callback..."
                      }
                      value={cancelReason}
                      onChange={setCancelReason}
                      width="100%"
                      height="size-1200"
                      UNSAFE_style={{ borderRadius: "4px" }}
                      maxLength={500}
                    />
                  </View>

                  <Flex justifyContent="end" gap="size-100" marginTop="size-200">
                    <Button variant="secondary" onPress={handleClose}>
                      Close
                    </Button>
                    <Button
                      variant="cta"
                      onPress={handleConfirm}
                      isDisabled={!cancelReason.trim() || isSubmitting}
                    >
                      {isSubmitting ? "Cancelling..." : "Confirm"}
                    </Button>
                  </Flex>
                </Flex>
              </View>
            </Content>
          </Dialog>
        )}
      </DialogContainer>
    </>
  );
};

export default CancelConfirmationDialog;
